import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {Badge} from 'antd';
import {MessageOutlined} from '@ant-design/icons';
import {selectTotalUnreadCount} from 'src/domain/app/redux/chat/chat-selectors';
import ChatRoomListPopup from './ChatRoomListPopup';
import ChatWindowPopup from './ChatWindowPopup';
import './styles.less';

const ChatPopup: React.FC = () => {
  const {messages} = useIntl();
  const totalUnreadCount = useSelector(selectTotalUnreadCount);
  const [isRoomListOpen, setIsRoomListOpen] = useState(false);
  const [openChatWindows, setOpenChatWindows] = useState<
    Array<{roomId: string; isMinimized: boolean}>
  >([]);

  const handleToggleRoomList = () => {
    setIsRoomListOpen(!isRoomListOpen);
  };

  const handleOpenChatWindow = (roomId: string) => {
    // Check if window is already open
    const existingWindow = openChatWindows.find((w) => w.roomId === roomId);
    if (existingWindow) {
      // If minimized, restore it
      if (existingWindow.isMinimized) {
        setOpenChatWindows(
          openChatWindows.map((w) =>
            w.roomId === roomId ? {...w, isMinimized: false} : w,
          ),
        );
      }
      return;
    }

    // Add new window (max 3 windows)
    if (openChatWindows.length >= 3) {
      // Remove the oldest minimized window or the oldest window
      const minimizedWindow = openChatWindows.find((w) => w.isMinimized);
      if (minimizedWindow) {
        setOpenChatWindows([
          ...openChatWindows.filter((w) => w.roomId !== minimizedWindow.roomId),
          {roomId, isMinimized: false},
        ]);
      } else {
        setOpenChatWindows([
          ...openChatWindows.slice(1),
          {roomId, isMinimized: false},
        ]);
      }
    } else {
      setOpenChatWindows([...openChatWindows, {roomId, isMinimized: false}]);
    }

    // Close room list
    setIsRoomListOpen(false);
  };

  const handleCloseChatWindow = (roomId: string) => {
    setOpenChatWindows(openChatWindows.filter((w) => w.roomId !== roomId));
  };

  const handleMinimizeChatWindow = (roomId: string) => {
    setOpenChatWindows(
      openChatWindows.map((w) =>
        w.roomId === roomId ? {...w, isMinimized: !w.isMinimized} : w,
      ),
    );
  };

  const handleBackToRoomList = (roomId: string) => {
    // Close the chat window
    handleCloseChatWindow(roomId);
    // Open the room list
    setIsRoomListOpen(true);
  };

  return (
    <div className="chat-popup-container">
      {/* Chat Windows */}
      {openChatWindows.map((window, index) => (
        <ChatWindowPopup
          key={window.roomId}
          roomId={window.roomId}
          isMinimized={window.isMinimized}
          onClose={() => handleCloseChatWindow(window.roomId)}
          onMinimize={() => handleMinimizeChatWindow(window.roomId)}
          onBack={() => handleBackToRoomList(window.roomId)}
          style={{
            right: `${80 + index * 330}px`,
          }}
        />
      ))}

      {/* Room List Popup */}
      {isRoomListOpen && (
        <ChatRoomListPopup
          onSelectRoom={handleOpenChatWindow}
          onClose={() => setIsRoomListOpen(false)}
        />
      )}

      {/* Chat Launcher Button */}
      {!isRoomListOpen && openChatWindows.length === 0 && (
        <div className="chat-launcher" onClick={handleToggleRoomList}>
          <Badge count={totalUnreadCount} overflowCount={99}>
            <div className="chat-launcher-icon">
              <MessageOutlined />
            </div>
          </Badge>
        </div>
      )}
    </div>
  );
};

export default ChatPopup;
